import { useApplicantAPI } from "@certn/api";
import {
  ButtonGroup,
  Checkbox,
  GhostButton,
  PrimaryButton,
  SectionMessage,
  Space,
  Text,
  Link,
  LinkButton,
  ModalTrigger,
} from "@certn/cascadia-react";
import { CascadiaJsonFormContext } from "@certn/forms";
import { RenderError } from "@certn/forms/src/components/helpers/RenderError";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { hasLabel } from "@certn/forms/src/utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms/src/wrappers/withCascadiaLayoutProps";
import { css } from "@emotion/css";
import { useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { SkipDbsConsent } from "./SkipDBSConsent";
import { SkipDBSConsentModal } from "./SkipDBSConsentModal";
import { useApplication } from "../../ApplicationContext";
import { useHandleChange } from "../../utils";

type DbsVariant = "basic" | "enhanced" | "standard";

interface DbsConsentFieldConfig {
  dataKey: string;
  fields: {
    hasAuthorized: string;
    hasReadPrivacyPolicy: string;
    hasConsentedToSharingResult: string;
    hasProvidedCompleteInfo: string;
    hasUnderstoodEligibility?: string;
  };
}

const DBS_VARIANT_CONFIGS: Record<DbsVariant, DbsConsentFieldConfig> = {
  basic: {
    dataKey: "dbs_consent",
    fields: {
      hasAuthorized: "has_authorized_certn_to_submit_application",
      hasReadPrivacyPolicy: "has_read_privacy_policy",
      hasConsentedToSharingResult: "has_consented_to_sharing_result",
      hasProvidedCompleteInfo: "has_provided_complete_and_true_information",
    },
  },
  enhanced: {
    dataKey: "enhanced_dbs_consent",
    fields: {
      hasAuthorized: "enhanced_has_authorized_certn_to_submit_application",
      hasReadPrivacyPolicy: "enhanced_has_read_privacy_policy",
      hasConsentedToSharingResult: "enhanced_has_consented_to_sharing_result",
      hasProvidedCompleteInfo:
        "enhanced_has_provided_complete_and_true_information",
      hasUnderstoodEligibility: "enhanced_has_understood_role_eligibility",
    },
  },
  standard: {
    dataKey: "standard_dbs_consent",
    fields: {
      hasAuthorized: "standard_has_authorized_certn_to_submit_application",
      hasReadPrivacyPolicy: "standard_has_read_privacy_policy",
      hasConsentedToSharingResult: "standard_has_consented_to_sharing_result",
      hasProvidedCompleteInfo:
        "standard_has_provided_complete_and_true_information",
      hasUnderstoodEligibility: "standard_has_understood_role_eligibility",
    },
  },
};

function detectVariant(data: Record<string, unknown> | undefined): DbsVariant {
  if (data?.enhanced_dbs_consent !== undefined) {
    return "enhanced";
  }
  if (data?.standard_dbs_consent !== undefined) {
    return "standard";
  }
  return "basic";
}

function DbsConsentControl(props: CascadiaLayoutProps) {
  const [onSkipConsent, setOnSkipConsent] = useState(false);
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const { t } = useTranslation();
  const { navigateToSection, prevSection } = useApplication();
  const { uischema, data } = props;
  const handleChange = useHandleChange();
  const { caseId } = useParams();
  const { data: caseDetail } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/`
  );

  const variant = useMemo(() => detectVariant(data), [data]);
  const config = DBS_VARIANT_CONFIGS[variant];
  const consentData = data?.[config.dataKey] as
    | Record<string, boolean>
    | undefined;

  const isEnhanced = variant === "enhanced";
  const isStandard = variant === "standard";
  // Enhanced and Standard DBS share title/info banner/footer copy; only Basic
  // differs significantly.
  const isRegulatedRoleCheck = isEnhanced || isStandard;

  if (isSummary) {
    return null;
  }

  if (!hasLabel(uischema)) {
    return <RenderError>Invalid Category</RenderError>;
  }

  const renderCheckbox = (
    id: string,
    isSelected: boolean,
    onChange: (value: boolean) => void,
    children: React.ReactNode
  ) => (
    <Checkbox testID={id} isSelected={isSelected} onChange={onChange}>
      {children}
    </Checkbox>
  );

  const fieldPath = (field: string) => `${config.dataKey}.${field}`;

  const allConsentsChecked =
    !!consentData?.[config.fields.hasAuthorized] &&
    !!consentData?.[config.fields.hasReadPrivacyPolicy] &&
    !!consentData?.[config.fields.hasConsentedToSharingResult] &&
    !!consentData?.[config.fields.hasProvidedCompleteInfo] &&
    (!config.fields.hasUnderstoodEligibility ||
      !!consentData?.[config.fields.hasUnderstoodEligibility]);

  const isNextButtonEnabled = onSkipConsent || allConsentsChecked;

  const renderBasicCheckboxes = () => (
    <>
      {renderCheckbox(
        fieldPath(config.fields.hasAuthorized),
        !!consentData?.[config.fields.hasAuthorized],
        (value) => handleChange(fieldPath(config.fields.hasAuthorized), value),
        <Text style="form/body-secondary">
          {t(
            "I consent and authorise Certn to submit an application for, and to obtain, process and view my basic disclosure certificate prior to me seeing it. Where applicable, I consent and authorise Certn to provide this information directly to {{clientName}} / someone I explicitly appoint or authorise.",
            {
              clientName: caseDetail?.customer_name,
            }
          )}
        </Text>
      )}
      {renderCheckbox(
        fieldPath(config.fields.hasReadPrivacyPolicy),
        !!consentData?.[config.fields.hasReadPrivacyPolicy],
        (value) =>
          handleChange(fieldPath(config.fields.hasReadPrivacyPolicy), value),
        <Trans>
          <Text style="form/body-secondary">
            I have read the{" "}
            <Link
              testID="dbs-privacy-policy-link"
              linkStyle="monochrome"
              textStyle="global/body-secondary"
              textColor="text-weak"
              href=" https://www.gov.uk/government/publications/basic-dbs-check-privacy-policy/basic-dbs-check-privacy-policy"
              target="_blank"
            >
              <Trans>Basic DBS Check Processing Privacy Policy</Trans>
            </Link>{" "}
            for Applicants and I understand how DBS will process my personal
            data.
          </Text>
        </Trans>
      )}
      {renderCheckbox(
        fieldPath(config.fields.hasConsentedToSharingResult),
        !!consentData?.[config.fields.hasConsentedToSharingResult],
        (value) =>
          handleChange(
            fieldPath(config.fields.hasConsentedToSharingResult),
            value
          ),
        <Trans>
          <Text style="form/body-secondary">
            I consent to the DBS providing an electronic result directly to the
            Responsible Organisation that submits my application (i.e. Certn UK
            Ltd). I understand that an electronic result contains a message that
            indicates either the certificate does not contain criminal record
            information or to await certificate which will indicate that my
            certificate contains criminal record information. In some cases the
            Responsible Organisation may provide this information directly to my
            employer prior to me receiving my certificate. I understand if I do
            not consent to an electronic result being issued to the responsible
            organisation submitting my application that I must not proceed with
            this application and I should apply directly to DBS{" "}
            <Link
              testID="dbs-privacy-policy-link"
              linkStyle="monochrome"
              textStyle="global/body-secondary"
              textColor="text-weak"
              href="https://www.gov.uk/request-copy-criminal-record"
              target="_blank"
            >
              Request a basic DBS check - GOV.UK (www.gov.uk)
            </Link>{" "}
            .
          </Text>
        </Trans>
      )}
      {renderCheckbox(
        fieldPath(config.fields.hasProvidedCompleteInfo),
        !!consentData?.[config.fields.hasProvidedCompleteInfo],
        (value) =>
          handleChange(fieldPath(config.fields.hasProvidedCompleteInfo), value),
        <Text style="form/body-secondary">
          <Trans>
            I have provided complete and true information in support of the
            application and I understand that knowingly making a false statement
            for this purpose is a criminal offence.
          </Trans>
        </Text>
      )}
    </>
  );

  const renderEnhancedCheckboxes = () => (
    <>
      {renderCheckbox(
        fieldPath(config.fields.hasReadPrivacyPolicy),
        !!consentData?.[config.fields.hasReadPrivacyPolicy],
        (value) =>
          handleChange(fieldPath(config.fields.hasReadPrivacyPolicy), value),
        <Text style="form/body-secondary">
          <Trans>I have read the</Trans>{" "}
          <Link
            testID="dbs-privacy-policy-link"
            linkStyle="monochrome"
            textStyle="global/body-secondary"
            textColor="text-weak"
            href="https://www.gov.uk/government/publications/standard-and-enhanced-dbs-check-privacy-policy/standard-and-enhanced-dbs-check-privacy-policy"
            target="_blank"
          >
            {t("DBS Check Processing Privacy Policy")}
          </Link>{" "}
          <Trans>
            for Applicants and understand how DBS will process my personal data.
          </Trans>
        </Text>
      )}
      {renderCheckbox(
        fieldPath(config.fields.hasProvidedCompleteInfo),
        !!consentData?.[config.fields.hasProvidedCompleteInfo],
        (value) =>
          handleChange(fieldPath(config.fields.hasProvidedCompleteInfo), value),
        <Text style="form/body-secondary">
          <Trans>
            I have provided complete and true information in support of this
            application and understand that knowingly making a false statement
            is a criminal offence.
          </Trans>
        </Text>
      )}
      {config.fields.hasUnderstoodEligibility &&
        renderCheckbox(
          fieldPath(config.fields.hasUnderstoodEligibility),
          !!consentData?.[config.fields.hasUnderstoodEligibility],
          (value) =>
            handleChange(
              fieldPath(config.fields.hasUnderstoodEligibility!),
              value
            ),
          <Text style="form/body-secondary">
            <Trans>
              I understand that this role is eligible for an Enhanced DBS check.
            </Trans>
          </Text>
        )}
      {renderCheckbox(
        fieldPath(config.fields.hasAuthorized),
        !!consentData?.[config.fields.hasAuthorized],
        (value) => handleChange(fieldPath(config.fields.hasAuthorized), value),
        <Text style="form/body-secondary">
          <Trans>
            I consent to Certn submitting an application to DBS on my behalf for
            an Enhanced DBS certificate, which may include spent and unspent
            convictions, other relevant police information, and barred list
            checks where applicable.
          </Trans>
        </Text>
      )}
      {renderCheckbox(
        fieldPath(config.fields.hasConsentedToSharingResult),
        !!consentData?.[config.fields.hasConsentedToSharingResult],
        (value) =>
          handleChange(
            fieldPath(config.fields.hasConsentedToSharingResult),
            value
          ),
        <Text style="form/body-secondary">
          <Trans>
            I consent to the DBS providing an electronic result directly to
            Certn as the Responsible Organisation submitting my application. I
            understand that an electronic result contains a message indicating
            either (i) the certificate does not contain criminal record
            information or (ii) to await the certificate (indicating that my
            certificate contains criminal record information). Certn may provide
            this information directly to my employer prior to me receiving my
            certificate. I understand that if I do not consent to Certn
            receiving an electronic result, I must not proceed with this
            application and I should apply directly to the DBS.
          </Trans>
        </Text>
      )}
    </>
  );

  const renderStandardCheckboxes = () => (
    <>
      {renderCheckbox(
        fieldPath(config.fields.hasReadPrivacyPolicy),
        !!consentData?.[config.fields.hasReadPrivacyPolicy],
        (value) =>
          handleChange(fieldPath(config.fields.hasReadPrivacyPolicy), value),
        <Text style="form/body-secondary">
          <Trans>I have read the</Trans>{" "}
          <Link
            testID="dbs-privacy-policy-link"
            linkStyle="monochrome"
            textStyle="global/body-secondary"
            textColor="text-weak"
            href="https://www.gov.uk/government/publications/standard-and-enhanced-dbs-check-privacy-policy/standard-and-enhanced-dbs-check-privacy-policy"
            target="_blank"
          >
            {t("Standard DBS Check Processing Privacy Policy")}
          </Link>{" "}
          <Trans>
            for Applicants and understand how DBS will process my personal data.
          </Trans>
        </Text>
      )}
      {renderCheckbox(
        fieldPath(config.fields.hasProvidedCompleteInfo),
        !!consentData?.[config.fields.hasProvidedCompleteInfo],
        (value) =>
          handleChange(fieldPath(config.fields.hasProvidedCompleteInfo), value),
        <Text style="form/body-secondary">
          <Trans>
            I have provided complete and true information in support of this
            application and understand that knowingly making a false statement
            is a criminal offence.
          </Trans>
        </Text>
      )}
      {config.fields.hasUnderstoodEligibility &&
        renderCheckbox(
          fieldPath(config.fields.hasUnderstoodEligibility),
          !!consentData?.[config.fields.hasUnderstoodEligibility],
          (value) =>
            handleChange(
              fieldPath(config.fields.hasUnderstoodEligibility!),
              value
            ),
          <Text style="form/body-secondary">
            <Trans>
              I understand and confirm that this role is eligible for a Standard
              DBS check.
            </Trans>
          </Text>
        )}
      {renderCheckbox(
        fieldPath(config.fields.hasAuthorized),
        !!consentData?.[config.fields.hasAuthorized],
        (value) => handleChange(fieldPath(config.fields.hasAuthorized), value),
        <Text style="form/body-secondary">
          <Trans>
            I consent to Certn submitting an application to DBS on my behalf for
            a Standard DBS certificate, which may include spent and unspent
            convictions and other relevant information.
          </Trans>
        </Text>
      )}
      {renderCheckbox(
        fieldPath(config.fields.hasConsentedToSharingResult),
        !!consentData?.[config.fields.hasConsentedToSharingResult],
        (value) =>
          handleChange(
            fieldPath(config.fields.hasConsentedToSharingResult),
            value
          ),
        <Text style="form/body-secondary">
          <Trans>
            I consent to the DBS providing an electronic result directly to
            Certn as the Responsible Organisation submitting my application. I
            understand that an electronic result contains a message indicating
            either (i) the certificate does not contain criminal record
            information or (ii) to await the certificate (indicating that my
            certificate contains criminal record information). Certn may provide
            this information directly to my employer prior to me receiving my
            certificate. I understand that if I do not consent to Certn
            receiving an electronic result, I must not proceed with this
            application and I should apply directly to the DBS.
          </Trans>
        </Text>
      )}
    </>
  );

  const renderCheckboxes = () => {
    if (isEnhanced) return renderEnhancedCheckboxes();
    if (isStandard) return renderStandardCheckboxes();
    return renderBasicCheckboxes();
  };

  const renderPageTitle = () => {
    if (isEnhanced) {
      return <Trans>Disclosure and Barring Service (Enhanced)</Trans>;
    }
    if (isStandard) {
      return <Trans>Disclosure and Barring Service (Standard)</Trans>;
    }
    return <Trans>DBS Consent</Trans>;
  };

  const renderIntro = () => {
    if (isEnhanced) {
      return (
        <Trans>
          To proceed with your Enhanced Disclosure and Barring Service ("DBS")
          criminal record check, please review and confirm the following
          statements:
        </Trans>
      );
    }
    if (isStandard) {
      return (
        <Trans>
          To proceed with your Standard Disclosure and Barring Service ("DBS")
          criminal record check, please review and confirm the following
          statements:
        </Trans>
      );
    }
    return (
      <Trans>
        To allow us to apply for your basic criminal record check from the DBS,
        please review the following statements and provide your consent by
        checking the boxes below.
      </Trans>
    );
  };

  const renderInfoBanner = () => {
    if (isRegulatedRoleCheck) {
      return (
        <Trans>
          If you do not give consent to any or all of the below Certn will not
          be able to submit your DBS application, in which case you can obtain a
          DBS check directly via
        </Trans>
      );
    }
    return (
      <Trans>
        If you do not give consent to any or all of the below Certn will not be
        able to submit your DBS application, in which case you can obtain a
        Basic DBS check directly via
      </Trans>
    );
  };

  return (
    <BaseLayout {...props} className={styles.layout}>
      <Text as="h1" style="form/page-title">
        {renderPageTitle()}
      </Text>
      {onSkipConsent && (
        <SkipDbsConsent
          clientName={caseDetail?.customer_name}
          variant={variant}
        />
      )}
      {!onSkipConsent && (
        <>
          <Text style="form/body-primary">{renderIntro()}</Text>
          <SectionMessage icon="info">
            {renderInfoBanner()}{" "}
            <Link
              testID="dbs-link"
              linkStyle="monochrome"
              textStyle="global/body-secondary"
              textColor="text-weak"
              href="http://www.gov.uk/DBS"
              target="_blank"
            >
              www.gov.uk/DBS
            </Link>
          </SectionMessage>
          {renderCheckboxes()}
          <Trans>
            <Text style="global/heading-small">
              <ModalTrigger
                trigger={
                  <LinkButton
                    linkStyle="monochrome"
                    textStyle="global/body-secondary-link"
                    textColor="text-weak"
                    testID="skip-dbs-consent-link"
                  >
                    {t("Click here")}
                  </LinkButton>
                }
              >
                <SkipDBSConsentModal
                  setOnSkipConsent={setOnSkipConsent}
                  variant={variant}
                />
              </ModalTrigger>{" "}
              to opt out of the processing of your data for this purpose.
            </Text>
          </Trans>
          {isRegulatedRoleCheck ? (
            <Text style="global/heading-small">
              <Trans>
                Any questions regarding how DBS processes personal information
                should be addressed to the DBS Data Protection Officer. You can
                also contact the Certn Data Protection Officer at
              </Trans>{" "}
              <Link
                testID="privacy-email"
                target="_blank"
                textStyle="global/body-secondary"
                textColor="text-weak"
                href="mailto:dpo@certn.co"
              >
                dpo@certn.co
              </Link>
              {"."}
            </Text>
          ) : (
            <Trans>
              <Text style="global/heading-small">
                You can obtain a copy of the DBS Code of Practice here or by
                contacting Certn's DPO. You can also contact the Certn DPO{" "}
                <Link
                  testID="privacy-email"
                  target="_blank"
                  textStyle="global/body-secondary"
                  textColor="text-weak"
                  href="mailto:dpo.emea@certn.co"
                >
                  dpo.emea@certn.co
                </Link>{" "}
                for any data protection query.
              </Text>
            </Trans>
          )}
        </>
      )}
      <div className={styles.actionBar}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              testID="section-back-button"
              leftIcon="chevron_left"
              onPress={async () => navigateToSection(prevSection)}
            >
              <Trans>Back</Trans>
            </GhostButton>
          )}
          <Space />
          {props.data && (
            <PrimaryButton
              testID="section-next-step-button"
              type="submit"
              isDisabled={!isNextButtonEnabled}
            >
              <Trans>Next step</Trans>
            </PrimaryButton>
          )}
        </ButtonGroup>
      </div>
    </BaseLayout>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  actionBar: css({
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
};

DbsConsentControl.displayName = "DBSConsent";

export const DbsConsentControlRenderer =
  withCascadiaLayoutProps(DbsConsentControl);

export type { DbsVariant };
